import { extractAffiliateNameSync } from "../lib/whitelistService";
import { calculateTieredCommission } from "./commissionTiers";

// Utility function to parse numbers from strings with currency formatting
export const parseNumber = (value) => {
  if (!value) return 0;
  const cleaned = String(value).replace(/[$,]/g, "");
  return parseFloat(cleaned) || 0;
};

// Format currency values
export const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

// Format percentage values
export const formatPercent = (value) => {
  return `${(value * 100).toFixed(2)}%`;
};

// Function to normalize ad names by removing "- Copy" and similar suffixes
export const normalizeAdName = (adName) => {
  if (!adName) return "";
  // Handle various dash types (hyphen, en dash, em dash) and copy patterns
  return adName
    .replace(/\s*[-–—]\s*Copy\s*(\d+)?$/i, "")
    .replace(/\s*[-–—]\s*copy\s*(\d+)?$/i, "")
    .replace(/\s*copy\s*(\d+)?$/i, "")
    .trim();
};

// Extract affiliate name from ad name using database whitelist
export const extractAffiliateName = (adName) => {
  // Use the dynamic database whitelist
  const dynamicResult = extractAffiliateNameSync(adName);

  // If dynamic whitelist returns a result, use it
  if (dynamicResult !== "Hercules Team") {
    return dynamicResult;
  }

  // Only fallback to legacy hardcoded patterns if database whitelist is not available
  if (!adName) return "Hercules Team";

  const adNameLower = adName.toLowerCase();

  // Check for specific ad patterns first (order matters - check longer patterns first)
  if (adNameLower.includes("kam111")) {
    return "kam111";
  }
  if (adNameLower.includes("ddiad")) {
    return "Dillon";
  }
  if (adNameLower.includes("hhorg")) {
    return "Dillon";
  }
  if (adNameLower.includes("dnah")) {
    return "Leaks";
  }
  if (adNameLower.includes("jsotto")) {
    return "Jemuel";
  }
  if (adNameLower.includes("jor")) {
    return "jor";
  }
  if (adNameLower.includes("tasdigital")) {
    return "TasDigital";
  }
  if (adNameLower.includes("gray")) {
    return "Gray";
  }
  if (adNameLower.includes("ori")) {
    return "Ori";
  }
  if (adNameLower.includes("zane")) {
    return "Zane";
  }
  if (adNameLower.includes("munsur")) {
    return "Munsur";
  }
  if (adNameLower.startsWith("dd")) {
    return "Dillon";
  }
  if (adNameLower.startsWith("t")) {
    return "Tarun";
  }

  return "Hercules Team";
};

// Validate required CSV columns
export const validateCSVColumns = (columns) => {
  const requiredColumns = ["Ad Name", "Ad Spend", "Pixel CV"];
  const missingColumns = requiredColumns.filter(
    (col) => !columns.includes(col)
  );

  // Check if we have either ROAS or Pixel ROAS
  if (!columns.includes("ROAS") && !columns.includes("Pixel ROAS")) {
    missingColumns.push("ROAS or Pixel ROAS");
  }

  return {
    isValid: missingColumns.length === 0,
    missingColumns,
    foundColumns: columns,
  };
};

// Process CSV data into affiliate statistics using tiered commission system
export const processAffiliateData = (csvData, commissionPercentage = null) => {
  const affiliateMap = new Map();
  let processedRows = 0;
  let skippedRows = 0;

  // First pass: group rows by affiliate and normalized ad name
  const affiliateAdMap = new Map(); // affiliate -> normalizedAdName -> aggregated data

  csvData.forEach((row, index) => {
    // Process every row - use defaults for missing values
    const adName = row["Ad Name"] || `Unknown Ad ${index + 1}`;
    const normalizedAdName = normalizeAdName(adName);
    const adSpend = parseNumber(row["Ad Spend"]);
    const affiliateName = extractAffiliateName(adName);
    const revenue = parseNumber(row["Pixel CV"]);
    const purchases = parseNumber(row["Pixel Purchases"]);

    // Track zero-spend rows but still process them for completeness
    if (adSpend <= 0) {
      skippedRows++; // Keep track for debugging, but still process the row
    }

    if (!affiliateAdMap.has(affiliateName)) {
      affiliateAdMap.set(affiliateName, new Map());
    }

    const affiliateAds = affiliateAdMap.get(affiliateName);

    if (!affiliateAds.has(normalizedAdName)) {
      affiliateAds.set(normalizedAdName, {
        originalAdName: adName, // Keep the first occurrence's name
        normalizedAdName,
        totalAdSpend: 0,
        totalRevenue: 0,
        totalPurchases: 0,
        rawData: row, // Keep original data for other fields
      });
    }

    const adData = affiliateAds.get(normalizedAdName);
    adData.totalAdSpend += adSpend;
    adData.totalRevenue += revenue;
    adData.totalPurchases += purchases;

    processedRows++;
  });

  // Second pass: build affiliate data and calculate commission on TOTAL aggregated amounts
  affiliateAdMap.forEach((affiliateAds, affiliateName) => {
    const affiliate = {
      name: affiliateName,
      ads: [],
      totalAdSpend: 0,
      totalRevenue: 0,
      totalProfit: 0,
      totalCommission: 0,
      totalPurchases: 0,
      adCount: 0,
      commissionBreakdown: {},
    };

    // First, aggregate all totals for the affiliate
    affiliateAds.forEach((adData, normalizedAdName) => {
      const { totalAdSpend, totalRevenue, totalPurchases, rawData } = adData;
      const roas = totalAdSpend > 0 ? totalRevenue / totalAdSpend : 0;
      const profit = totalRevenue - totalAdSpend;

      affiliate.ads.push({
        adName: normalizedAdName, // Use normalized name
        adSpend: totalAdSpend,
        revenue: totalRevenue,
        roas,
        profit,
        commission: 0, // Will be calculated proportionally below
        purchases: totalPurchases,
        cpa: rawData["Pixel CPA"],
        aov: rawData["Pixel AOV"],
        campaignName: rawData["Campaign Name"],
        adsetName: rawData["Adset Name"],
        commissionTier: null, // Will be set based on affiliate aggregated ROAS
        commissionLabel: null,
      });

      affiliate.totalAdSpend += totalAdSpend;
      affiliate.totalRevenue += totalRevenue;
      affiliate.totalProfit += profit;
      affiliate.totalPurchases += totalPurchases;
      affiliate.adCount += 1;
    });

    // CORRECTED: Calculate commission on affiliate's TOTAL aggregated ROAS (matches dashboard)
    const affiliateROAS = affiliate.totalAdSpend > 0 ? affiliate.totalRevenue / affiliate.totalAdSpend : 0;
    const affiliateCommissionResult = calculateTieredCommission(affiliateROAS, affiliate.totalAdSpend);
    affiliate.totalCommission = affiliateCommissionResult.commission;

    // Set commission breakdown based on affiliate's aggregated tier
    const tierLabel = affiliateCommissionResult.tier ? affiliateCommissionResult.tier.label : "No tier";
    affiliate.commissionBreakdown[tierLabel] = {
      count: affiliate.adCount,
      totalSpend: affiliate.totalAdSpend,
      totalCommission: affiliate.totalCommission,
      tier: affiliateCommissionResult.tier,
    };

    // Distribute commission proportionally across ads for display purposes
    affiliate.ads.forEach((ad) => {
      const proportion = affiliate.totalAdSpend > 0 ? ad.adSpend / affiliate.totalAdSpend : 0;
      ad.commission = affiliate.totalCommission * proportion;
      ad.commissionTier = affiliateCommissionResult.tier;
      ad.commissionLabel = affiliateCommissionResult.label;
    });

    // Calculate weighted average ROAS and commission rate
    if (affiliate.totalAdSpend > 0) {
      affiliate.avgROAS = affiliate.totalRevenue / affiliate.totalAdSpend;
      affiliate.avgCommissionRate =
        affiliate.totalCommission / affiliate.totalAdSpend;
    } else {
      affiliate.avgROAS = 0;
      affiliate.avgCommissionRate = 0;
    }

    affiliateMap.set(affiliateName, affiliate);
  });

  return {
    affiliates: Array.from(affiliateMap.values()),
    processedRows,
    skippedRows,
  };
};

// Calculate summary statistics
export const calculateSummaryStats = (processedData) => {
  if (!processedData) return null;

  const totals = processedData.reduce(
    (acc, affiliate) => ({
      totalAdSpend: acc.totalAdSpend + affiliate.totalAdSpend,
      totalRevenue: acc.totalRevenue + affiliate.totalRevenue,
      totalProfit: acc.totalProfit + affiliate.totalProfit,
      totalCommission: acc.totalCommission + affiliate.totalCommission,
      profitableAffiliates:
        acc.profitableAffiliates + (affiliate.totalProfit > 0 ? 1 : 0),
    }),
    {
      totalAdSpend: 0,
      totalRevenue: 0,
      totalProfit: 0,
      totalCommission: 0,
      profitableAffiliates: 0,
    }
  );

  return {
    ...totals,
    totalAffiliates: processedData.length,
    avgROAS:
      totals.totalAdSpend > 0 ? totals.totalRevenue / totals.totalAdSpend : 0,
    avgCommissionRate:
      totals.totalAdSpend > 0
        ? totals.totalCommission / totals.totalAdSpend
        : 0,
  };
};

// Sort data by specified key and direction
export const sortData = (data, sortConfig) => {
  if (!data || !sortConfig.key) return data;

  const sorted = [...data];
  sorted.sort((a, b) => {
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];

    if (sortConfig.direction === "asc") {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  return sorted;
};

// Date utility functions for consistent default date ranges
// All date calculations use local timezone to avoid conversion issues

// Format date in local timezone as YYYY-MM-DD
const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Get date range for "Past X Days" excluding today
export const getDefaultDateRange = (days = 7) => {
  const today = new Date();
  const todayYear = today.getFullYear();
  const todayMonth = today.getMonth();
  const todayDate = today.getDate();
  
  // End date: yesterday (exclude today)
  const endDate = new Date(todayYear, todayMonth, todayDate - 1);
  // Start date: days back from today
  const startDate = new Date(todayYear, todayMonth, todayDate - days);
  
  const result = {
    start: formatLocalDate(startDate),
    end: formatLocalDate(endDate)
  };
  
  
  
  return result;
};

// Check if current date range matches a "Past X Days" pattern
export const isPastDaysRange = (dateRangeStart, dateRangeEnd, days) => {
  const today = new Date();
  const todayYear = today.getFullYear();
  const todayMonth = today.getMonth();
  const todayDate = today.getDate();
  
  // Calculate expected range for "Past X Days"
  const expectedEnd = new Date(todayYear, todayMonth, todayDate - 1);
  const expectedStart = new Date(todayYear, todayMonth, todayDate - days);
  
  return dateRangeStart === formatLocalDate(expectedStart) && 
         dateRangeEnd === formatLocalDate(expectedEnd);
};

// Get default date filter state for components
export const getDefaultDateFilterState = (days = 7) => {
  const dateRange = getDefaultDateRange(days);
  
  return {
    dateFilterType: "range",
    singleDate: "",
    dateRangeStart: dateRange.start,
    dateRangeEnd: dateRange.end
  };
};

export function getAffiliateAdNameConditions(affiliateName) {
  const name = affiliateName.toLowerCase();
  const conditions = [];

  if (name === "dillon") {
    conditions.push(
      `ad_name.ilike.%ddiad%`,
      `ad_name.ilike.%hhorg%`,
      `ad_name.ilike.dd%`
    );
  }
  if (name === "jemuel") {
    conditions.push(`ad_name.ilike.%jsotto%`, `ad_name.ilike.%jor%`);
  }
  if (name === "leaks") {
    conditions.push(`ad_name.ilike.%dnah%`);
  }
  if (name === "tasdigital") {
    conditions.push(`ad_name.ilike.%tasdigital%`);
  }
  if (name === "tarun") {
    conditions.push(`ad_name.ilike.t%`);
  }

  // Add the affiliate name itself as a condition
  conditions.push(`ad_name.ilike.%${name}%`);

  // If no conditions, return a clause that matches nothing
  if (conditions.length === 0) {
    return "1=0";
  }

  return conditions.join(",");
}
